import { useCallback } from 'react';
import { useEffect } from 'react';
import { useRef } from 'react';
import { buildSignOutTarget } from './sign-out-target';
import { createBroadcastChannelFactory } from './broadcast-channel-factory';
import { createCrossTabBroadcaster } from './session-core';
import { sessionChannelName } from './session-core';
import type { CrossTabBroadcaster } from './session-core';

export { sessionChannelName };

type UseSessionBroadcastOptions = {
  onThemeChange?: (value: string) => void;
  onLogout?: () => void;
  onLogin?: () => void;
};

export function useSessionBroadcast(options?: UseSessionBroadcastOptions) {
  const broadcasterRef = useRef<CrossTabBroadcaster | null>(null);
  const onThemeChangeRef = useRef<UseSessionBroadcastOptions['onThemeChange']>(undefined);
  const onLogoutRef = useRef<UseSessionBroadcastOptions['onLogout']>(undefined);
  const onLoginRef = useRef<UseSessionBroadcastOptions['onLogin']>(undefined);
  onThemeChangeRef.current = options ? options.onThemeChange : undefined;
  onLogoutRef.current = options ? options.onLogout : undefined;
  onLoginRef.current = options ? options.onLogin : undefined;

  const broadcastLogout = useCallback(() => {
    if (broadcasterRef.current) {
      broadcasterRef.current.broadcastLogout();
    }
  }, []);

  const broadcastLogin = useCallback(() => {
    if (broadcasterRef.current) {
      broadcasterRef.current.broadcastLogin();
    }
  }, []);

  const broadcastTheme = useCallback((value: string) => {
    if (broadcasterRef.current) {
      broadcasterRef.current.broadcastTheme(value);
    }
  }, []);

  useEffect(() => {
    const broadcaster = createCrossTabBroadcaster({
      channelName: sessionChannelName,
      createChannel: createBroadcastChannelFactory,
      onLogout: () => {
        if (onLogoutRef.current) {
          onLogoutRef.current();
        }
      },
      onLogin: () => {
        if (onLoginRef.current) {
          onLoginRef.current();
        }
      },
      onThemeChange: (value) => {
        if (onThemeChangeRef.current) {
          onThemeChangeRef.current(value);
        }
      },
    });
    broadcasterRef.current = broadcaster;
    return () => {
      broadcaster.close();
      broadcasterRef.current = null;
    };
  }, []);

  return {
    broadcastLogout: broadcastLogout,
    broadcastLogin: broadcastLogin,
    broadcastTheme: broadcastTheme,
  };
}

export function broadcastLogout(): void {
  const broadcaster = createCrossTabBroadcaster({
    channelName: sessionChannelName,
    createChannel: createBroadcastChannelFactory,
  });
  broadcaster.broadcastLogout();
  broadcaster.close();
}

export function notifyUnauthenticated(redirectUrl: string): void {
  broadcastLogout();
  if (typeof window !== 'undefined') {
    window.location.assign(buildSignOutTarget(redirectUrl));
  }
}
